import type { PerfCounts, PerfReport } from "odyssey-shared";
import { say } from "../../words.ts";
import type { FrameSummary } from "./frames.ts";

/**
 * What the performance panel shows (ADR-087 decisions 2 and 7 and its build notes for P1.9-T137), as rows of words:
 * the player's half from the page's own measurements, the counts of what the page holds for a staff viewer alone, and
 * the server half from the last report while it is fresh. Pure, so the wording is tested apart from the panel.
 *
 * A count of the players, creatures or things near a player tells them who is close before they are seen, and the
 * count of game objects and the bytes received move with those, so none of the five is in what an ordinary player's
 * panel is given.
 */

/**
 * The page's measurements at one update: its frames, the world scene's game objects, the props loaded, what is in
 * view by kind (other players, creatures, things), the bytes received a second (null before two updates) and the
 * ping (null before a keepalive came back).
 */
export interface ClientSample {
  readonly frames: FrameSummary;
  readonly objects: number;
  readonly props: number;
  readonly players: number;
  readonly creatures: number;
  readonly things: number;
  readonly bytesPerSecond: number | null;
  readonly pingMs: number | null;
}

/**
 * One row: a label and its value.
 */
export interface PerfRow {
  readonly label: string;
  readonly value: string;
}

/**
 * One row of the zone and area table: a label, the zone's value and the area's.
 */
export interface PerfTableRow {
  readonly label: string;
  readonly zone: string;
  readonly area: string;
}

/**
 * The panel's content: the rows every player sees, the staff rows (null for a viewer who is not staff), and the
 * server rows and table once a fresh report has come.
 */
export interface PerfView {
  readonly client: readonly PerfRow[];
  readonly staff: readonly PerfRow[] | null;
  readonly server: { readonly rows: readonly PerfRow[]; readonly table: readonly PerfTableRow[] } | null;
}

/**
 * The panel's words. Drafts for the author (ADR-087): the title, the halves' headings, the table's columns and each
 * row's label. `client` holds the labels of everything the page itself measures, the rows every player sees and the
 * staff viewer's counts alike.
 */
export const PERF_WORDING = {
  title: say("perf.title"),
  server: say("perf.server"),
  zone: say("perf.zone"),
  area: say("perf.area"),
  unknown: say("perf.unknown"),
  client: { fps: say("perf.client.fps"), frame: say("perf.client.frame"), objects: say("perf.client.objects"), props: say("perf.client.props"), players: say("perf.client.players"), creatures: say("perf.client.creatures"), things: say("perf.client.things"), received: say("perf.client.received"), ping: say("perf.client.ping") },
  rows: { tick: say("perf.rows.tick"), clients: say("perf.rows.clients"), perClient: say("perf.rows.perClient"), near: say("perf.rows.near"), tiers: say("perf.rows.tiers") },
  table: { characters: say("perf.table.characters"), creatures: say("perf.table.creatures"), awake: say("perf.table.awake"), things: say("perf.table.things") },
} as const;

/**
 * A pair of times in milliseconds, one decimal each.
 *
 * @param a The first, as the median.
 * @param b The second, as the 99th percentile.
 * @returns The words.
 * @example
 * pairMs(16.66, 18.3); // "16.7 / 18.3 ms"
 */
function pairMs(a: number, b: number): string {
  return `${a.toFixed(1)} / ${b.toFixed(1)} ms`;
}

/**
 * Bytes a second in kibibytes a second, one decimal.
 *
 * @param bytes Bytes a second.
 * @returns The words.
 * @example
 * kbps(4300); // "4.2 KB/s"
 */
function kbps(bytes: number): string {
  return `${(bytes / 1024).toFixed(1)} KB/s`;
}

/**
 * The rows every player sees: the frames, the props loaded and the ping. Nothing here counts what is near the
 * player or moves with it: the bytes received, which rise when a character nearby moves, are a staff viewer's.
 *
 * @param sample The page's measurements.
 * @returns The rows, in order.
 */
export function clientRows(sample: ClientSample): PerfRow[] {
  const words = PERF_WORDING.client;
  return [
    { label: words.fps, value: String(Math.round(sample.frames.fps)) },
    { label: words.frame, value: pairMs(sample.frames.p50, sample.frames.p99) },
    { label: words.props, value: String(sample.props) },
    { label: words.ping, value: sample.pingMs === null ? PERF_WORDING.unknown : `${Math.round(sample.pingMs)} ms` },
  ];
}

/**
 * The rows a staff viewer alone sees: the scene's game objects, the other players, the creatures and the things the
 * client view holds, and the bytes received a second, which follow how much moves near the viewer.
 *
 * @param sample The page's measurements.
 * @returns The rows, in order.
 */
export function staffRows(sample: ClientSample): PerfRow[] {
  const words = PERF_WORDING.client;
  return [
    { label: words.objects, value: String(sample.objects) },
    { label: words.players, value: String(sample.players) },
    { label: words.creatures, value: String(sample.creatures) },
    { label: words.things, value: String(sample.things) },
    { label: words.received, value: sample.bytesPerSecond === null ? PERF_WORDING.unknown : kbps(sample.bytesPerSecond) },
  ];
}

/**
 * The server half from a report: the tick, the clients and their downstream bytes, the GM's near players and view
 * tiers, and the map server's counts beside the GM's chunk neighbourhood.
 *
 * @param report The report.
 * @returns The rows and the table.
 */
export function serverHalf(report: PerfReport): { readonly rows: PerfRow[]; readonly table: PerfTableRow[] } {
  const words = PERF_WORDING.rows;
  const rows: PerfRow[] = [
    { label: words.tick, value: pairMs(report.tickP50Us / 1000, report.tickP99Us / 1000) },
    { label: words.clients, value: String(report.clients) },
    { label: words.perClient, value: `${(report.downP50 / 1024).toFixed(1)} / ${kbps(report.downMax)}` },
    { label: words.near, value: String(report.near) },
    { label: words.tiers, value: `${report.full} / ${report.quarter}` },
  ];
  const table = (Object.keys(PERF_WORDING.table) as (keyof PerfCounts)[]).map((key) => ({ label: PERF_WORDING.table[key], zone: String(report.zone[key]), area: String(report.area[key]) }));
  return { rows, table };
}

/**
 * The panel's content.
 *
 * @param sample The page's measurements.
 * @param report The last report while it is fresh, or null.
 * @param staff Whether the gateway offered this connection the server half, which it does only for a viewer allowed it.
 * @returns The view.
 * @example
 * perfView(sample, null, false).staff; // null: a player's panel holds no count of what is near
 */
export function perfView(sample: ClientSample, report: PerfReport | null, staff: boolean): PerfView {
  return { client: clientRows(sample), staff: staff ? staffRows(sample) : null, server: report === null ? null : serverHalf(report) };
}
